import { Link } from "react-router-dom";
import type { ClaimCandidate } from "@hp/types";
import { ClaimStatusPill } from "../ClaimStatusPill";
import { ClaimValueTableCells } from "./ClaimValueTableCells";
import { formatINR } from "../../lib/workbenchFormat";
import { claimValueParts } from "../../lib/claimValue";

interface ClaimGroupMemberRowsProps {
  members: ClaimCandidate[];
  detailPath: (claimId: string) => string;
  colSpan: number;
}

export function ClaimGroupMemberRows({ members, detailPath, colSpan }: ClaimGroupMemberRowsProps) {
  const sorted = [...members].sort((a, b) => (a.financial_year ?? "").localeCompare(b.financial_year ?? ""));

  return (
    <tr className="bg-slate-50/80">
      <td colSpan={colSpan} className="px-5 py-0">
        <div className="border-t border-slate-200 py-3">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
            Financial year tranches
          </p>
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="text-slate-500">
                <th className="pb-2 pr-4 font-medium">FY</th>
                <th className="pb-2 pr-4 font-medium">Finding</th>
                <th className="pb-2 pr-4 font-medium">Principal</th>
                <th className="pb-2 pr-4 font-medium">LPS</th>
                <th className="pb-2 pr-4 font-medium">Total</th>
                <th className="pb-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.map((member) => (
                <tr key={member.id}>
                  <td className="py-2 pr-4 font-mono tabular-nums text-slate-700">
                    {member.financial_year ?? "—"}
                  </td>
                  <td className="py-2 pr-4">
                    <Link
                      to={detailPath(member.id)}
                      className="font-mono text-brand-700 hover:underline"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {member.finding_case_number ?? member.finding_id.slice(0, 8)}
                    </Link>
                  </td>
                  <ClaimValueTableCells
                    valueLow={member.value_low}
                    valueHigh={member.value_high}
                    breakdown={member.value_breakdown}
                  />
                  <td className="py-2">
                    <Link to={detailPath(member.id)} onClick={(event) => event.stopPropagation()}>
                      <ClaimStatusPill status={member.status} counselValidatedAt={member.counsel_validated_at} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-200 text-slate-700">
                <td className="pt-2 pr-4 font-medium" colSpan={2}>Group total</td>
                <td className="pt-2 pr-4 font-mono tabular-nums font-medium">
                  {formatINR(sorted.reduce((sum, m) => sum + claimValueParts(m.value_low, m.value_high, m.value_breakdown).principalHigh, 0))}
                </td>
                <td className="pt-2 pr-4 font-mono tabular-nums">
                  {formatINR(sorted.reduce((sum, m) => sum + claimValueParts(m.value_low, m.value_high, m.value_breakdown).carryingHigh, 0))}
                </td>
                <td className="pt-2 pr-4 font-mono tabular-nums font-semibold">
                  {formatINR(sorted.reduce((sum, m) => sum + m.value_high, 0))}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </td>
    </tr>
  );
}
